import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Calendar, BarChart3, List, Plus, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Filter, RefreshCw, CheckCircle2, Clock, XCircle, Users, FileText, Send, AlertTriangle, Download
} from 'lucide-react';
import { trackingReportApi, editorApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { TrackingLogCard } from '../components/TrackingLogCard';
import { ManualEntryModal } from '../components/ManualEntryModal';
import { TrackingLogDetailModal } from '../components/TrackingLogDetailModal';

interface WorkLog {
  id: string;
  editor_id: string;
  request_id: string | null;
  request_type: string;
  vertical: string | null;
  title: string | null;
  assigned_creatives: number;
  completed_creatives: number;
  status: string;
  wl_status: string;
  started_at: string;
  completed_at: string | null;
  date_bucket: string;
  created_at: string;
  editor_name: string;
  editor_display_name: string;
  notes_count: number;
  latest_note: string | null;
  last_note_at: string | null;
  request_created_at: string | null;
  fre_status: string | null;
  request_removed: boolean;
}

interface CalendarDay {
  date_bucket: string;
  total_logs: number;
  active_count: number;
  completed_count: number;
  cancelled_count: number;
  reassigned_count: number;
  total_assigned: number;
  total_completed: number;
}

interface CalendarBar {
  id: string;
  editor_id: string;
  request_id: string | null;
  title: string | null;
  status: string;
  assigned_creatives: number;
  completed_creatives: number;
  start_date: string;
  end_date: string;
  editor_name: string;
  editor_display_name: string;
}

interface SummaryData {
  total_logs: number;
  active_logs: number;
  completed_logs: number;
  unique_editors: number;
  total_assigned: number;
  total_completed: number;
  today_logs: number;
  today_completed: number;
}

interface EditorSummary {
  editor_id: string;
  editor_name: string;
  editor_display_name: string;
  total_logs: number;
  active_logs: number;
  completed_logs: number;
  total_assigned: number;
  total_completed: number;
  last_activity: string | null;
}

interface DailyMatrixRow {
  editor_id: string;
  editor_name: string;
  day: string;
  uploads_count: number;
  uploads_videos: number;
  uploads_images: number;
  uploads_other: number;
  uploads_bytes: number;
  tasks_completed: number;
  tasks_closed_no_delivery: number;
  creatives_completed: number;
}

/** Parse date_bucket from DB (could be ISO or YYYY-MM-DD) into YYYY-MM-DD */
function parseDateBucket(d: string): string {
  if (!d) return '';
  return d.split('T')[0];
}

function formatDate(d: string): string {
  const parsed = parseDateBucket(d);
  if (!parsed) return '-';
  const dt = new Date(parsed + 'T12:00:00');
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function toDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

/** Format bytes as MB/GB */
function formatBytes(bytes: number): string {
  const n = Number(bytes) || 0;
  if (n <= 0) return '0 MB';
  const mb = n / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 2 : 1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

/** Get bar color class based on status + progress */
function getBarColor(status: string, completed: number, assigned: number): string {
  if (status === 'completed') return 'bg-emerald-500';          // green
  if (status === 'reassigned') return 'bg-slate-300 dark:bg-slate-600'; // light gray
  if (status === 'cancelled') return 'bg-orange-400';           // orange
  // active status
  if (completed > 0 && assigned > 0) return 'bg-amber-400';    // yellow (in progress)
  return 'bg-rose-400';                                          // red (no progress)
}

/** Get status badge style for list/detail views */
function getStatusBadge(status: string) {
  switch (status) {
    case 'completed': return { bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle2 };
    case 'reassigned': return { bg: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400', icon: RefreshCw };
    case 'cancelled': return { bg: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', icon: XCircle };
    case 'request_removed': return { bg: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', icon: AlertTriangle };
    default: return { bg: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', icon: Clock };
  }
}

// ============ Inline Note Input Component ============
function InlineNoteInput({ logId, onSaved }: { logId: string; onSaved: () => void }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSave = async () => {
    if (!value.trim() || saving) return;
    setSaving(true);
    try {
      await trackingReportApi.addNote(logId, { note: value.trim() });
      setValue('');
      onSaved();
    } catch (err) {
      console.error('Failed to add note', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
        placeholder="Add note..."
        className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
      />
      {value.trim() && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="p-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
        >
          <Send size={12} />
        </button>
      )}
    </div>
  );
}

// ============ Multi-select Editor Checkbox Dropdown ============
function EditorMultiSelect({
  editors,
  selectedIds,
  onChange,
}: {
  editors: { id: string; name: string; display_name: string }[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (id: string) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);
  };

  const label = selectedIds.length === 0
    ? 'All Editors'
    : selectedIds.length === 1
    ? (editors.find(e => e.id === selectedIds[0])?.display_name || editors.find(e => e.id === selectedIds[0])?.name || '1 editor')
    : `${selectedIds.length} editors`;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1 text-sm border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600 min-w-[140px] justify-between"
      >
        <span className="truncate">{label}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {/* Clear all */}
          {selectedIds.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="w-full text-left px-3 py-2 text-xs text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b border-gray-100 dark:border-gray-700"
            >
              Clear selection (show all)
            </button>
          )}
          {editors.map(ed => (
            <label
              key={ed.id}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(ed.id)}
                onChange={() => toggle(ed.id)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-800 dark:text-gray-200 truncate">{ed.display_name || ed.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ MAIN PAGE ============
export function TrackingReportPage() {
  const { user } = useAuth();
  const additionalRoles: string[] = (user as any)?.additional_roles || [];
  const ADMIN_LIKE_ROLES = ['admin', 'team_lead', 'ceo', 'head_media_buying', 'creative_head', 'assistant_team_lead'];
  const canSeeAllEditors = ADMIN_LIKE_ROLES.includes(user?.role || '') ||
    additionalRoles.some((r: string) => ADMIN_LIKE_ROLES.includes(r));

  const [viewMode, setViewMode] = useState<'calendar' | 'list' | 'daily'>('list');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // Multi-select editors — default set after fetching user's editor
  const [selectedEditorIds, setSelectedEditorIds] = useState<string[]>([]);
  const [myEditorId, setMyEditorId] = useState<string | null>(null);
  const [editorInitDone, setEditorInitDone] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('active_completed');
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return toDateStr(d);
  });
  const [dateTo, setDateTo] = useState<string>(toDateStr(new Date()));
  const [datePreset, setDatePreset] = useState<string>('30d');

  const [logs, setLogs] = useState<WorkLog[]>([]);
  const [calendarData, setCalendarData] = useState<CalendarDay[]>([]);
  const [calendarBars, setCalendarBars] = useState<CalendarBar[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [editorsSummary, setEditorsSummary] = useState<EditorSummary[]>([]);
  const [allEditors, setAllEditors] = useState<{ id: string; name: string; display_name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Daily Work view (per-day, per-editor uploads + completed tasks matrix)
  const [dailyMatrix, setDailyMatrix] = useState<DailyMatrixRow[]>([]);
  const [dailyPage, setDailyPage] = useState(0);   // Daily Work is paginated by day (client-side)
  const [dailyMatrixLoading, setDailyMatrixLoading] = useState(false);
  const [dailyMatrixError, setDailyMatrixError] = useState('');
  // Files drill-down modal (click an uploads cell/badge → files + request/buyer/cost/link/test status)
  const [fileModal, setFileModal] = useState<{ editor_id: string; editor_name: string; day: string; file_type?: 'video' | 'image' } | null>(null);
  const [modalFiles, setModalFiles] = useState<any[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalErr, setModalErr] = useState('');
  const [modalPreview, setModalPreview] = useState<any | null>(null);   // the file being played/opened inline
  const openFiles = async (editor_id: string, editor_name: string, day: string, file_type?: 'video' | 'image') => {
    setFileModal({ editor_id, editor_name, day, file_type });
    setModalFiles([]); setModalPreview(null); setModalErr(''); setModalLoading(true);
    try {
      const res = await trackingReportApi.getEditorDayFiles({ editor_id, day, ...(file_type ? { file_type } : {}) });
      setModalFiles(res.data?.data || []);
    } catch (e: any) {
      setModalErr(e?.response?.data?.error || 'Failed to load files.');
    } finally { setModalLoading(false); }
  };

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [dayLogs, setDayLogs] = useState<WorkLog[]>([]);
  const [dayLoading, setDayLoading] = useState(false);

  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntryDate, setManualEntryDate] = useState<string | undefined>(undefined);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  // Accordion state for multi-editor grouping
  const [collapsedEditors, setCollapsedEditors] = useState<Set<string>>(new Set());

  // List view pagination (by date groups)
  const DATES_PER_PAGE = 7;
  const [listPage, setListPage] = useState(0);

  const applyPreset = (preset: string) => {
    setDatePreset(preset);
    const now = new Date();
    let from: Date;
    switch (preset) {
      case '7d': from = new Date(now); from.setDate(from.getDate() - 7); break;
      case '30d': from = new Date(now); from.setDate(from.getDate() - 30); break;
      case '90d': from = new Date(now); from.setDate(from.getDate() - 90); break;
      default: return;
    }
    setDateFrom(toDateStr(from));
    setDateTo(toDateStr(now));
  };

  const getFilterParams = useCallback(() => {
    const params: any = {};
    if (selectedEditorIds.length > 0) params.editor_ids = selectedEditorIds.join(',');
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    return params;
  }, [selectedEditorIds, statusFilter, dateFrom, dateTo]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    setListPage(0);
    try {
      const params = getFilterParams();
      const promises: Promise<any>[] = [
        trackingReportApi.getSummary(params),
        trackingReportApi.getCalendar(params),
        trackingReportApi.getLogs({ ...params, limit: 500 }),
      ];
      // Only fetch bars for calendar view when <=5 editors selected
      if (selectedEditorIds.length > 0 && selectedEditorIds.length <= 5) {
        promises.push(trackingReportApi.getCalendarBars(params));
      }

      const results = await Promise.all(promises);

      setSummary(results[0].data.data.summary);
      setEditorsSummary(results[0].data.data.editors);
      setCalendarData(results[1].data.data);
      setLogs(results[2].data.data.logs);
      if (results[3]) {
        setCalendarBars(results[3].data.data);
      } else {
        setCalendarBars([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load tracking data');
    } finally {
      setLoading(false);
    }
  }, [getFilterParams, selectedEditorIds.length]);

  // Fetch current user's editor and set default filter
  useEffect(() => {
    trackingReportApi.getMyEditor().then(res => {
      const ed = res.data.data;
      if (ed) {
        setMyEditorId(ed.id);
        // Default to own editor unless admin/ATL
        if (!canSeeAllEditors) {
          setSelectedEditorIds([ed.id]);
        }
      }
      setEditorInitDone(true);
    }).catch(() => setEditorInitDone(true));

    if (canSeeAllEditors) {
      editorApi.getAll().then(res => {
        const eds = (res.data.data || res.data).filter((e: any) => e.is_active);
        setAllEditors(eds);
      }).catch(() => {});
    }
  }, [canSeeAllEditors]);

  useEffect(() => { if (editorInitDone) fetchData(); }, [fetchData, editorInitDone]);

  const fetchDailyMatrix = useCallback(async () => {
    setDailyMatrixLoading(true);
    setDailyMatrixError('');
    try {
      const params: any = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (selectedEditorIds.length > 0) params.editor_ids = selectedEditorIds.join(',');
      const res = await trackingReportApi.getDailyMatrix(params);
      setDailyMatrix(res.data.data);
    } catch (err: any) {
      setDailyMatrixError(err.response?.data?.error || 'Failed to load daily work data');
    } finally {
      setDailyMatrixLoading(false);
    }
  }, [dateFrom, dateTo, selectedEditorIds]);

  useEffect(() => {
    if (editorInitDone && viewMode === 'daily') fetchDailyMatrix();
  }, [editorInitDone, viewMode, fetchDailyMatrix]);

  const handleDayClick = async (dateStr: string) => {
    if (selectedDay === dateStr) { setSelectedDay(null); return; }
    setSelectedDay(dateStr);
    setDayLoading(true);
    try {
      const params: any = { from: dateStr, to: dateStr, limit: 50 };
      if (selectedEditorIds.length > 0) params.editor_ids = selectedEditorIds.join(',');
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await trackingReportApi.getLogs(params);
      setDayLogs(res.data.data.logs);
    } catch (err) { setDayLogs([]); }
    finally { setDayLoading(false); }
  };

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  // Build calendar
  const calendarMap: Record<string, CalendarDay> = {};
  calendarData.forEach(d => { calendarMap[parseDateBucket(d.date_bucket)] = d; });

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const todayStr = toDateStr(new Date());

  // Use bar view when <=5 editors selected, otherwise dot view
  const useBarView = selectedEditorIds.length > 0 && selectedEditorIds.length <= 5 && calendarBars.length > 0;

  // Build bar layout: which bars appear on which rows across days
  const barRows: CalendarBar[] = [];
  if (useBarView) {
    const seen = new Set<string>();
    calendarBars.forEach((bar) => {
      if (!seen.has(bar.id)) {
        seen.add(bar.id);
        barRows.push(bar);
      }
    });
  }

  // Group logs by date for list view
  // Active logs appear on EVERY day from assignment date through today
  // Completed logs appear from assignment date through completion date
  const logsByDate: Record<string, WorkLog[]> = {};
  const sortedDatesSet = new Set<string>();

  // Helper: add one day to YYYY-MM-DD
  const addDay = (dateStr: string): string => {
    const d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  logs.forEach(log => {
    const startDate = parseDateBucket(log.date_bucket);
    if (!startDate) return;

    // Determine end date based on effective status (consider both wl_status and fre_status)
    const effectiveStatus = getEffectiveStatus(log);
    let endDate: string;
    if (effectiveStatus === 'completed') {
      // Completed: show from assignment through completion day
      endDate = log.completed_at ? parseDateBucket(log.completed_at) :
                log.last_note_at ? parseDateBucket(log.last_note_at) :
                startDate;
      if (endDate < startDate) endDate = startDate;
    } else if (effectiveStatus === 'active' || (log.wl_status === 'active' && effectiveStatus !== 'reassigned' && effectiveStatus !== 'request_removed' && effectiveStatus !== 'cancelled')) {
      // Active: show every day through today
      endDate = todayStr;
    } else {
      // reassigned/cancelled/request_removed — show only on their specific date_bucket date (no carry-forward)
      const dayKey = startDate;
      if (dayKey >= (dateFrom || '') && dayKey <= (dateTo || '9999-99-99')) {
        if (!logsByDate[dayKey]) { logsByDate[dayKey] = []; sortedDatesSet.add(dayKey); }
        if (!logsByDate[dayKey].some(l => l.id === log.id)) {
          logsByDate[dayKey].push(log);
        }
      }
      return;
    }

    // Cap to visible date range (don't generate dates outside filter)
    const visibleStart = dateFrom && startDate < dateFrom ? dateFrom : startDate;
    const visibleEnd = dateTo && endDate > dateTo ? dateTo : endDate;

    // Add log to every day from visibleStart through visibleEnd
    let cursor = visibleStart;
    while (cursor <= visibleEnd) {
      if (!logsByDate[cursor]) { logsByDate[cursor] = []; sortedDatesSet.add(cursor); }
      if (!logsByDate[cursor].some(l => l.id === log.id)) {
        logsByDate[cursor].push(log);
      }
      cursor = addDay(cursor);
    }
  });

  // Add ALL dates in the selected range so empty days appear in the list view
  if (dateFrom && dateTo) {
    let cursor = dateFrom;
    while (cursor <= dateTo) {
      sortedDatesSet.add(cursor);
      cursor = addDay(cursor);
    }
  }

  const sortedDates = Array.from(sortedDatesSet).sort((a, b) => b.localeCompare(a));

  const [exporting, setExporting] = useState(false);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params: any = {};
      if (selectedEditorIds.length > 0) params.editor_ids = selectedEditorIds.join(',');
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;

      const response = await trackingReportApi.exportExcel(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Creative_Team_Summary_${dateFrom || 'start'}_to_${dateTo || 'present'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExporting(false);
    }
  };

  // Group by editor within each date (for accordion)
  const multiEditorMode = selectedEditorIds.length > 1;
  const toggleEditorCollapse = (key: string) => {
    setCollapsedEditors(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Group daily-matrix rows by day (newest first) for the Daily Work view
  const dailyMatrixByDay: Record<string, DailyMatrixRow[]> = {};
  dailyMatrix.forEach(row => {
    const day = parseDateBucket(row.day);
    if (!day) return;
    if (!dailyMatrixByDay[day]) dailyMatrixByDay[day] = [];
    dailyMatrixByDay[day].push(row);
  });
  const dailyMatrixDates = Object.keys(dailyMatrixByDay).sort((a, b) => b.localeCompare(a));
  const DAILY_PAGE_SIZE = 10;   // days per page
  const dailyTotalPages = Math.max(1, Math.ceil(dailyMatrixDates.length / DAILY_PAGE_SIZE));
  const pagedDailyDates = dailyMatrixDates.slice(dailyPage * DAILY_PAGE_SIZE, dailyPage * DAILY_PAGE_SIZE + DAILY_PAGE_SIZE);
  useEffect(() => { setDailyPage(0); }, [dailyMatrix]);   // new data (filter change) → back to first page

  return (
    <DashboardLayout
      title="Tracking Report"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportExcel}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium disabled:opacity-50"
            title="Export as Excel"
          >
            <Download size={16} /> {exporting ? 'Exporting...' : 'Export Excel'}
          </button>
          <button
            onClick={() => { setManualEntryDate(undefined); setShowManualEntry(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
          >
            <Plus size={16} /> Manual Entry
          </button>
          <button onClick={fetchData} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" title="Refresh">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1"><Clock size={16} /><span className="text-xs font-medium uppercase">Active</span></div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.active_logs}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.today_logs} today</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-green-500 mb-1"><CheckCircle2 size={16} /><span className="text-xs font-medium uppercase">Completed</span></div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.completed_logs}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.today_completed} today</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-blue-500 mb-1"><FileText size={16} /><span className="text-xs font-medium uppercase">Total Creatives</span></div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.total_completed}<span className="text-sm text-gray-400">/{summary.total_assigned}</span></p>
              <p className="text-xs text-gray-500 mt-1">{Number(summary.total_assigned) > 0 ? Math.round((Number(summary.total_completed) / Number(summary.total_assigned)) * 100) : 0}% completion rate</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-purple-500 mb-1"><Users size={16} /><span className="text-xs font-medium uppercase">Editors</span></div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{summary.unique_editors}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.total_logs} total logs</p>
            </div>
          </div>
        )}

        {/* Filters Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-3 items-center">
            <Filter className="text-gray-500" size={18} />

            {/* View Toggle */}
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
              <button onClick={() => setViewMode('calendar')} className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${viewMode === 'calendar' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>
                <Calendar size={14} /> Calendar
              </button>
              <button onClick={() => setViewMode('list')} className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>
                <List size={14} /> List
              </button>
              <button onClick={() => setViewMode('daily')} className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${viewMode === 'daily' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>
                <FileText size={14} /> Daily Work
              </button>
            </div>

            <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />

            {/* Date Presets */}
            <div className="flex gap-1">
              {[{ key: '7d', label: '7 days' }, { key: '30d', label: '30 days' }, { key: '90d', label: '90 days' }].map(p => (
                <button key={p.key} onClick={() => applyPreset(p.key)}
                  className={`px-2.5 py-1 text-xs rounded-md font-medium transition ${datePreset === p.key ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                  {p.label}
                </button>
              ))}
            </div>

            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setDatePreset('custom'); }}
              className="px-2 py-1 text-sm border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setDatePreset('custom'); }}
              className="px-2 py-1 text-sm border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white" />

            {/* Multi-select Editor Filter */}
            {canSeeAllEditors && allEditors.length > 0 && (
              <EditorMultiSelect
                editors={allEditors}
                selectedIds={selectedEditorIds}
                onChange={setSelectedEditorIds}
              />
            )}

            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="px-2 py-1 text-sm border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white">
              <option value="active_completed">Active + Completed</option>
              <option value="active">Active Only</option>
              <option value="completed">Completed Only</option>
              <option value="all">All (incl. Reassigned)</option>
            </select>
          </div>
        </div>

        {/* Main Content */}
        {loading && !summary ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
          </div>
        ) : viewMode === 'calendar' ? (
          /* ============ CALENDAR VIEW ============ */
          <div className="flex gap-6">
            <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${selectedDay ? 'flex-1' : 'w-full'}`}>
              {/* Month nav */}
              <div className="flex items-center justify-between mb-4">
                <button onClick={prevMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><ChevronLeft size={20} /></button>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{monthNames[currentMonth]} {currentYear}</h2>
                <button onClick={nextMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><ChevronRight size={20} /></button>
              </div>

              {/* Color Legend — always visible */}
              <div className="flex items-center justify-center gap-6 mb-3 py-2.5 px-4 bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-100 dark:border-gray-700">
                <span className="font-semibold text-gray-500 dark:text-gray-400 uppercase text-[10px] tracking-wider">Bar Colors:</span>
                <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                  <div className="w-6 h-2 rounded-full bg-emerald-500" />
                  <span>Completed</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                  <div className="w-6 h-2 rounded-full bg-amber-400" />
                  <span>In Progress</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                  <div className="w-6 h-2 rounded-full bg-rose-400" />
                  <span>Assigned (No Work Done)</span>
                </div>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">{day}</div>
                ))}
              </div>

              {/* Calendar cells */}
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }).map((_, i) => (
                  <div key={`empty-${i}`} className="min-h-[80px]" />
                ))}

                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const dayData = calendarMap[dateStr];
                  const isToday = dateStr === todayStr;
                  const isSelected = dateStr === selectedDay;

                  // Get bars that span this day (only active + completed)
                  const dayBars = useBarView
                    ? barRows.filter(bar => dateStr >= bar.start_date && dateStr <= bar.end_date
                        && bar.status !== 'cancelled' && bar.status !== 'reassigned')
                    : [];

                  return (
                    <button
                      key={day}
                      onClick={() => handleDayClick(dateStr)}
                      className={`min-h-[80px] rounded-lg p-1.5 text-left transition relative flex flex-col
                        ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}
                        ${isToday && !isSelected ? 'bg-blue-50/60 dark:bg-blue-900/10' : ''}
                        ${!isSelected && !isToday ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50' : ''}
                      `}
                    >
                      <span className={`text-xs font-medium ${isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                        {day}
                      </span>

                      {/* Bar view: colored bars per request based on status */}
                      {useBarView && dayBars.length > 0 ? (
                        <div className="flex-1 flex flex-col gap-[3px] mt-1 overflow-hidden">
                          {dayBars.slice(0, 5).map(bar => {
                            const barBg = getBarColor(bar.status, bar.completed_creatives, bar.assigned_creatives);
                            const isStart = dateStr === bar.start_date;
                            const isEnd = dateStr === bar.end_date;
                            return (
                              <div
                                key={bar.id}
                                className={`h-[7px] ${barBg}
                                  ${isStart && isEnd ? 'rounded-full mx-0.5' : isStart ? 'rounded-l-full ml-0.5' : isEnd ? 'rounded-r-full mr-0.5' : ''}
                                `}
                                title={`${bar.title || 'Untitled'} (${bar.editor_display_name || bar.editor_name}) — ${bar.completed_creatives}/${bar.assigned_creatives} ${bar.status}`}
                              />
                            );
                          })}
                          {dayBars.length > 5 && (
                            <span className="text-[8px] text-gray-400">+{dayBars.length - 5} more</span>
                          )}
                        </div>
                      ) : dayData ? (
                        /* Dot view: fallback for many editors or no bar data */
                        <div className="flex-1 flex flex-col justify-end gap-0.5 mt-0.5">
                          <div className="flex gap-0.5 flex-wrap">
                            {Number(dayData.completed_count) > 0 && <div className="w-2 h-2 rounded-full bg-green-500" title={`${dayData.completed_count} completed`} />}
                            {Number(dayData.active_count) > 0 && <div className="w-2 h-2 rounded-full bg-yellow-500" title={`${dayData.active_count} active`} />}
                            {Number(dayData.cancelled_count) > 0 && <div className="w-2 h-2 rounded-full bg-red-500" title={`${dayData.cancelled_count} cancelled`} />}
                          </div>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {dayData.total_logs} log{Number(dayData.total_logs) !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Day Panel */}
            {selectedDay && (
              <div className="w-96 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 overflow-y-auto max-h-[calc(100vh-280px)]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{formatDate(selectedDay)}</h3>
                  <button onClick={() => setSelectedDay(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                    <XCircle size={18} className="text-gray-400" />
                  </button>
                </div>
                {dayLoading ? (
                  <div className="flex justify-center py-8"><RefreshCw className="animate-spin text-blue-500" size={20} /></div>
                ) : dayLogs.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No work logs for this day</p>
                ) : (
                  <div className="space-y-3">
                    {dayLogs.map(log => (
                      <TrackingLogCard key={log.id} log={log} onClick={() => setSelectedLogId(log.id)} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : viewMode === 'list' ? (
          /* ============ LIST VIEW — GROUPED BY DATE ============ */
          <div className="space-y-4">
            {/* Editor Overview Cards */}
            {canSeeAllEditors && editorsSummary.length > 0 && selectedEditorIds.length === 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Editor Overview</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                  {editorsSummary.map(ed => (
                    <button key={ed.editor_id} onClick={() => setSelectedEditorIds([ed.editor_id])}
                      className="text-left p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{ed.editor_display_name || ed.editor_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-yellow-600">{ed.active_logs} active</span>
                        <span className="text-xs text-green-600">{ed.completed_logs} done</span>
                      </div>
                      <div className="mt-1">
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                          <div className="bg-green-500 h-1.5 rounded-full"
                            style={{ width: `${Number(ed.total_assigned) > 0 ? Math.min((Number(ed.total_completed) / Number(ed.total_assigned)) * 100, 100) : 0}%` }} />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Date-grouped logs with pagination */}
            {sortedDates.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400">
                No dates in the selected range
              </div>
            ) : (
              <>
              {/* Pagination header */}
              {sortedDates.length > DATES_PER_PAGE && (
                <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2.5">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Showing days {listPage * DATES_PER_PAGE + 1}–{Math.min((listPage + 1) * DATES_PER_PAGE, sortedDates.length)} of {sortedDates.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setListPage(p => Math.max(0, p - 1))}
                      disabled={listPage === 0}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      <ChevronLeft size={14} /> Newer
                    </button>
                    <button
                      onClick={() => setListPage(p => Math.min(Math.ceil(sortedDates.length / DATES_PER_PAGE) - 1, p + 1))}
                      disabled={(listPage + 1) * DATES_PER_PAGE >= sortedDates.length}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      Older <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
              {sortedDates.slice(listPage * DATES_PER_PAGE, (listPage + 1) * DATES_PER_PAGE).map(date => {
                const dateLogs = logsByDate[date] || [];
                const isEmpty = dateLogs.length === 0;

                // Group by editor for accordion
                const editorGroups: Record<string, WorkLog[]> = {};
                dateLogs.forEach(log => {
                  const key = log.editor_id;
                  if (!editorGroups[key]) editorGroups[key] = [];
                  editorGroups[key].push(log);
                });
                const editorKeys = Object.keys(editorGroups);

                return (
                  <div key={date} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {/* Date Header */}
                    <div className="bg-gray-50 dark:bg-gray-900/50 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                          {formatDate(date)}
                        </h3>
                        <span className="text-xs text-gray-500">{isEmpty ? 'No logs' : `${dateLogs.length} log${dateLogs.length !== 1 ? 's' : ''}`}</span>
                      </div>
                      <button
                        onClick={() => { setManualEntryDate(date); setShowManualEntry(true); }}
                        className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                      >
                        <Plus size={12} /> Add Entry
                      </button>
                    </div>

                    {/* Empty day placeholder */}
                    {isEmpty ? (
                      <div className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
                        No work logged for this day
                      </div>
                    ) : multiEditorMode && editorKeys.length > 1 ? (
                      /* If multi-editor: accordion per editor within this date */
                      editorKeys.map(editorId => {
                        const editorLogs = editorGroups[editorId];
                        const editorName = editorLogs[0].editor_display_name || editorLogs[0].editor_name;
                        const collapseKey = `${date}-${editorId}`;
                        const isCollapsed = collapsedEditors.has(collapseKey);

                        return (
                          <div key={collapseKey}>
                            {/* Editor accordion header */}
                            <button
                              onClick={() => toggleEditorCollapse(collapseKey)}
                              className="w-full flex items-center justify-between px-4 py-2 bg-gray-25 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition"
                            >
                              <div className="flex items-center gap-2">
                                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{editorName}</span>
                                <span className="text-xs text-gray-400">({editorLogs.length})</span>
                              </div>
                            </button>

                            {!isCollapsed && (
                              <div>
                                {renderLogRows(editorLogs, false)}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      /* Single editor or all editors: flat rows */
                      renderLogRows(dateLogs, multiEditorMode || selectedEditorIds.length === 0)
                    )}
                  </div>
                );
              })}
              {/* Bottom pagination */}
              {sortedDates.length > DATES_PER_PAGE && (
                <div className="flex items-center justify-center gap-2 py-3">
                  <button
                    onClick={() => setListPage(p => Math.max(0, p - 1))}
                    disabled={listPage === 0}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft size={14} /> Newer
                  </button>
                  <span className="text-sm text-gray-500 dark:text-gray-400 px-3">
                    Page {listPage + 1} of {Math.ceil(sortedDates.length / DATES_PER_PAGE)}
                  </span>
                  <button
                    onClick={() => setListPage(p => Math.min(Math.ceil(sortedDates.length / DATES_PER_PAGE) - 1, p + 1))}
                    disabled={(listPage + 1) * DATES_PER_PAGE >= sortedDates.length}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Older <ChevronRight size={14} />
                  </button>
                </div>
              )}
              </>
            )}
          </div>
        ) : (
          /* ============ DAILY WORK VIEW — GROUPED BY DAY, ONE ROW PER EDITOR ============ */
          <div className="space-y-4">
            {dailyMatrixLoading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="animate-spin text-blue-600" size={32} />
              </div>
            ) : dailyMatrixError ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-600 dark:text-red-400">{dailyMatrixError}</p>
              </div>
            ) : dailyMatrixDates.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center text-gray-500 dark:text-gray-400">
                No work logged in the selected range
              </div>
            ) : (
              <>
              {pagedDailyDates.map(day => {
                const dayRows = dailyMatrixByDay[day];
                return (
                  <div key={day} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {/* Date Header */}
                    <div className="bg-gray-50 dark:bg-gray-900/50 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{formatDate(day)}</h3>
                      <span className="text-xs text-gray-500">{dayRows.length} editor{dayRows.length !== 1 ? 's' : ''}</span>
                    </div>

                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-900/30">
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Editor</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Uploads</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Size</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tasks Completed</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase" title="Completed assignments that delivered zero creatives (close-outs, not production)">Closed (no delivery)</th>
                          <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Creatives Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dayRows.map(row => (
                          <tr key={`${day}-${row.editor_id}`} className="border-b border-gray-50 dark:border-gray-700/30 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
                            <td className="px-3 py-2.5 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">{row.editor_name}</td>
                            <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              {Number(row.uploads_count) > 0 ? (
                                <span className="inline-flex items-center gap-1.5">
                                  {/* Click the count → all files; a badge → filtered by type. Opens the drill-down modal. */}
                                  <button type="button" onClick={() => openFiles(row.editor_id, row.editor_name, day)}
                                    className="font-semibold text-primary hover:underline" title="View delivered files">
                                    {Number(row.uploads_count)}
                                  </button>
                                  {Number(row.uploads_videos) > 0 && (
                                    <button type="button" onClick={() => openFiles(row.editor_id, row.editor_name, day, 'video')}
                                      className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 text-[10px] font-medium hover:ring-1 hover:ring-blue-400 cursor-pointer" title="View videos">
                                      {Number(row.uploads_videos)} video
                                    </button>
                                  )}
                                  {Number(row.uploads_images) > 0 && (
                                    <button type="button" onClick={() => openFiles(row.editor_id, row.editor_name, day, 'image')}
                                      className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400 text-[10px] font-medium hover:ring-1 hover:ring-purple-400 cursor-pointer" title="View images">
                                      {Number(row.uploads_images)} image
                                    </button>
                                  )}
                                  {Number(row.uploads_other) > 0 && (
                                    <button type="button" onClick={() => openFiles(row.editor_id, row.editor_name, day)}
                                      className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 text-[10px] font-medium hover:ring-1 hover:ring-gray-400 cursor-pointer" title="Other file types">
                                      {Number(row.uploads_other)} other
                                    </button>
                                  )}
                                </span>
                              ) : (
                                <span className="font-medium text-gray-900 dark:text-white">0</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatBytes(Number(row.uploads_bytes))}</td>
                            <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{Number(row.tasks_completed)}</td>
                            <td className={`px-3 py-2.5 text-xs whitespace-nowrap ${Number(row.tasks_closed_no_delivery) > 0 ? 'text-amber-600 dark:text-amber-500' : 'text-gray-600 dark:text-gray-400'}`}>{Number(row.tasks_closed_no_delivery)}</td>
                            <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{Number(row.creatives_completed)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
              {dailyTotalPages > 1 && (
                <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
                  <button
                    onClick={() => setDailyPage(p => Math.max(0, p - 1))}
                    disabled={dailyPage === 0}
                    className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                    Previous
                  </button>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Page {dailyPage + 1} of {dailyTotalPages} · {dailyMatrixDates.length} day{dailyMatrixDates.length !== 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={() => setDailyPage(p => Math.min(dailyTotalPages - 1, p + 1))}
                    disabled={dailyPage >= dailyTotalPages - 1}
                    className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                    Next
                  </button>
                </div>
              )}
              </>
            )}
          </div>
        )}

        {showManualEntry && (
          <ManualEntryModal
            onClose={() => { setShowManualEntry(false); setManualEntryDate(undefined); }}
            onCreated={() => { setShowManualEntry(false); setManualEntryDate(undefined); fetchData(); }}
            editors={canSeeAllEditors ? allEditors : []}
            canSelectEditor={canSeeAllEditors}
            defaultDate={manualEntryDate}
          />
        )}
        {selectedLogId && (
          <TrackingLogDetailModal logId={selectedLogId} onClose={() => setSelectedLogId(null)} onUpdated={fetchData} />
        )}
        {/* Files drill-down — request, media buyer, cost, inline preview + test status; all in this modal. */}
        {fileModal && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setFileModal(null)}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">{fileModal.editor_name} · delivered files</div>
                  <div className="text-xs text-gray-500">{fileModal.day}{fileModal.file_type ? ` · ${fileModal.file_type}s only` : ''}</div>
                </div>
                <button onClick={() => setFileModal(null)} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 text-lg leading-none">✕</button>
              </div>
              {modalPreview && (
                <div className="border-b border-gray-200 dark:border-gray-700 bg-black/90 p-3 flex flex-col items-center">
                  {modalPreview.file_type === 'image'
                    ? <img src={modalPreview.play_url} alt={modalPreview.filename} className="max-h-[42vh] max-w-full rounded object-contain" />
                    : <video src={modalPreview.play_url} controls autoPlay className="max-h-[42vh] max-w-full rounded bg-black" />}
                  <div className="mt-2 flex items-center gap-3 text-xs text-gray-300">
                    <span className="truncate max-w-[280px]">{modalPreview.filename}</span>
                    {modalPreview.play_url && <a href={modalPreview.play_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">open ↗</a>}
                    <button onClick={() => setModalPreview(null)} className="hover:text-white">close preview</button>
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {modalLoading && <div className="text-center text-sm text-gray-500 py-8">Loading files…</div>}
                {modalErr && <div className="text-center text-sm text-red-500 py-8">{modalErr}</div>}
                {!modalLoading && !modalErr && modalFiles.length === 0 && <div className="text-center text-sm text-gray-500 py-8">No files for this day.</div>}
                {modalFiles.map(f => (
                  <div key={f.id} className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:border-primary/40">
                    <button onClick={() => setModalPreview(f)} disabled={!f.play_url}
                      className="shrink-0 w-16 h-16 rounded bg-gray-100 dark:bg-gray-700 flex flex-col items-center justify-center gap-0.5 text-[10px] text-gray-500 hover:ring-2 hover:ring-primary disabled:opacity-50" title="Open / play here">
                      <span className="text-lg">{f.file_type === 'image' ? '🖼️' : '▶'}</span>{f.file_type}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{f.filename}</div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate">
                        {f.request_title ? <>Request: <span className="text-gray-700 dark:text-gray-300">{f.request_title}</span></> : <span className="italic">no linked request</span>}
                        {f.media_buyer && <> · Buyer: <span className="text-gray-700 dark:text-gray-300">{f.media_buyer}</span></>}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {formatBytes(f.file_size)}{f.duration ? ` · ${Math.round(f.duration)}s` : ''}
                        {f.estimated_cost_usd != null && (
                          <span title={f.request_estimated_total != null
                            ? `This file's share of the $${Number(f.request_estimated_total).toFixed(2)} request estimate`
                              + (f.request_creatives_count ? `, split across ${f.request_creatives_count} creatives by pricing (images flat, videos by duration)` : '')
                            : undefined}>
                            {' '}· est. ${Number(f.estimated_cost_usd).toFixed(2)}
                          </span>
                        )}
                        {f.actual_cost_usd != null && <> · actual ${Number(f.actual_cost_usd).toFixed(2)}</>}
                        {f.play_url && <> · <button onClick={() => setModalPreview(f)} className="text-primary hover:underline">open ↗</button></>}
                      </div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-0.5">
                      {/* Ad-verified launch status (filename found in a spending external ad) */}
                      {f.launch_status && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${f.launch_status === 'launched'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'}`}
                          title={f.launch_status === 'launched'
                            ? `Launched — ran as ad "${f.launch_ad_name || ''}"${f.launch_spend != null ? ` · $${Number(f.launch_spend).toFixed(0)} spend` : ''}`
                            : 'Not launched — filename not found in any spending ad in the dashboard'}>
                          {f.launch_status === 'launched' ? '✓ launched' : '✗ not launched'}
                        </span>
                      )}
                      {(() => {
                        // Drive the test badge from the ad-verified launch status: launched → tested,
                        // not-launched → untested. Fall back to the real QA test_status only when launch
                        // hasn't been checked (so it no longer sits at 'pending' once launch is known).
                        const s: string = f.launch_status === 'launched' ? 'tested'
                          : f.launch_status === 'not_launched' ? 'untested'
                          : (f.test_status || 'pending');
                        const map: Record<string, string> = {
                          tested: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
                          untested: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
                          scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
                          pending: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
                        };
                        return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${map[s] || map.pending}`}
                          title={s === 'pending' ? 'QA test status — separate from ad-verified launch' : (f.roi != null ? `ROI ${f.roi}${f.platform ? ' · ' + f.platform : ''}` : (f.platform || ''))}>
                          {s === 'pending' ? 'test: pending' : `test: ${s}`}
                        </span>;
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );

  // Helper: render log rows for list view table
  // Helper: get effective status — prefer fre_status (source of truth) over work log status
  function getEffectiveStatus(log: WorkLog): string {
    if (log.request_removed) return 'request_removed';
    if (log.fre_status === 'completed') return 'completed';
    if (log.fre_status === 'reassigned') return 'reassigned';
    const cc = Number(log.completed_creatives) || 0;
    const ac = Number(log.assigned_creatives) || 0;
    if (log.status === 'completed' || (cc > 0 && ac > 0 && cc >= ac)) return 'completed';
    return log.status;
  }

  // Helper: get row border color based on request progress
  function getRowBorderColor(log: WorkLog): string {
    const s = getEffectiveStatus(log);
    if (s === 'completed') return 'border-l-4 border-l-emerald-500';
    if (s === 'reassigned') return 'border-l-4 border-l-slate-300';
    if (s === 'cancelled') return 'border-l-4 border-l-orange-400';
    if (s === 'request_removed') return 'border-l-4 border-l-red-400';
    if (Number(log.completed_creatives) > 0 && Number(log.assigned_creatives) > 0) return 'border-l-4 border-l-amber-400';
    return 'border-l-4 border-l-rose-400';
  }

  function renderLogRows(rowLogs: WorkLog[], showEditor: boolean) {
    return (
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-900/30">
            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Requested</th>
            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Assigned</th>
            {showEditor && <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Editor</th>}
            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title / Request</th>
            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vertical</th>
            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase min-w-[160px]">Notes</th>
            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Progress</th>
            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
            <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Note</th>
          </tr>
        </thead>
        <tbody>
          {rowLogs.map(log => (
            <tr key={log.id} className={`border-b border-gray-50 dark:border-gray-700/30 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition ${getRowBorderColor(log)}`}>
              <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {log.request_created_at ? formatDate(log.request_created_at) : '-'}
              </td>
              <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {formatDate(log.date_bucket)}
              </td>
              {showEditor && (
                <td className="px-3 py-2.5 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                  {log.editor_display_name || log.editor_name}
                </td>
              )}
              <td className="px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 max-w-[180px] cursor-pointer" onClick={() => setSelectedLogId(log.id)}>
                <span className="hover:underline font-medium">{log.title || 'Untitled'}</span>
              </td>
              <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {log.vertical || '-'}
              </td>
              <td className="px-3 py-2.5 min-w-[160px]">
                {log.latest_note && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 truncate max-w-[200px]" title={log.latest_note}>
                    {log.latest_note}
                    {Number(log.notes_count) > 1 && <span className="text-gray-400 ml-1">(+{Number(log.notes_count) - 1})</span>}
                  </p>
                )}
                <InlineNoteInput logId={log.id} onSaved={fetchData} />
              </td>
              <td className="px-3 py-2.5">
                {log.request_type !== 'manual' ? (
                  <div className="flex items-center gap-2">
                    <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div className={`h-2 rounded-full ${getBarColor(log.status, Number(log.completed_creatives), Number(log.assigned_creatives))}`}
                        style={{ width: `${Number(log.assigned_creatives) > 0 ? Math.min((Number(log.completed_creatives) / Number(log.assigned_creatives)) * 100, 100) : 0}%` }} />
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{Number(log.completed_creatives)}/{Number(log.assigned_creatives)}</span>
                  </div>
                ) : <span className="text-xs text-gray-400">-</span>}
              </td>
              <td className="px-3 py-2.5">
                {(() => {
                  const effStatus = getEffectiveStatus(log);
                  const badge = getStatusBadge(effStatus);
                  const Icon = badge.icon;
                  return (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg}`}>
                      <Icon size={12} />
                      {effStatus}
                    </span>
                  );
                })()}
              </td>
              <td className="px-3 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {log.last_note_at ? new Date(log.last_note_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
}
